import type { UTCDate } from '@date-fns/utc';
import { format } from 'date-fns';

import {
  type DateOption,
  DatePresets,
  type DateRangeOption,
  DateRangePresets,
} from '../../constants/datePresets';
import type {
  DateGranularity,
  WeekMonthDateRanges,
} from '../../contexts/FilterProvider';

export const identifyDateRangePreset = (
  rawDateRangeFilter: { min: UTCDate | null; max: UTCDate | null },
  viewGranularity: DateGranularity,
  availableDateRange: WeekMonthDateRanges
) => {
  if (!rawDateRangeFilter.min && !rawDateRangeFilter.max) {
    return 'max';
  }

  const formatStr = viewGranularity === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd';
  const formatFct = (date: UTCDate) => format(date, formatStr);

  const maxEnd = availableDateRange[viewGranularity].max;
  const minStart = availableDateRange[viewGranularity].min;

  for (const { value, getDateRange } of DateRangePresets) {
    if (!getDateRange || value === 'custom' || value === 'max') continue;

    const { start, end } = getDateRange(maxEnd, minStart);

    if (
      start &&
      end &&
      rawDateRangeFilter.min &&
      formatFct(rawDateRangeFilter.min) === formatFct(start) &&
      (rawDateRangeFilter.max === null ||
        (rawDateRangeFilter.max &&
          formatFct(rawDateRangeFilter.max) === formatFct(end)))
    ) {
      return value;
    }
  }

  return 'custom';
};

export const identifyDatePreset = (
  rawDateFilter: { value: UTCDate | null },
  viewGranularity: DateGranularity,
  availableDateRange: WeekMonthDateRanges
) => {
  if (!rawDateFilter.value) {
    return 'latest';
  }

  const formatStr = viewGranularity === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd';
  const formatFct = (date: UTCDate) => format(date, formatStr);

  for (const { value, getDate } of DatePresets) {
    if (!getDate || value === 'custom' || value === 'max') continue;

    const maxEnd = availableDateRange[viewGranularity].max;
    const date = getDate(maxEnd);

    if (date && formatFct(rawDateFilter.value) === formatFct(date)) {
      return value;
    }
  }

  return 'custom';
};

export const computeDateFromOption = (
  option: DateOption,
  availableDateRange: WeekMonthDateRanges,
  viewGranularity: DateGranularity
): UTCDate | null => {
  if (option.getDate) {
    return option.getDate(availableDateRange[viewGranularity].max);
  }
  return null;
};

export const computeDateRangeFromOption = (
  option: DateRangeOption,
  availableDateRange: WeekMonthDateRanges,
  viewGranularity: DateGranularity
): { start: UTCDate; end: UTCDate } | null => {
  if (option.getDateRange) {
    return option.getDateRange(
      availableDateRange[viewGranularity].max,
      availableDateRange[viewGranularity].min
    );
  }
  return null;
};

export const computeInitialDates = (
  selectedPreset: DateRangeOption,
  availableDateRange: WeekMonthDateRanges,
  viewGranularity: DateGranularity
): { startDate: UTCDate | null; endDate: UTCDate | null } => {
  if (selectedPreset.value === 'custom') {
    return {
      startDate: availableDateRange[viewGranularity].min,
      endDate: availableDateRange[viewGranularity].max,
    };
  }

  if (selectedPreset.value === 'max') {
    return { startDate: null, endDate: null };
  }

  const computed = computeDateRangeFromOption(
    selectedPreset,
    availableDateRange,
    viewGranularity
  );

  if (computed) {
    let finalStartDate: UTCDate | null =
      computed.start < availableDateRange[viewGranularity].min
        ? availableDateRange[viewGranularity].min
        : computed.start;
    let finalEndDate: UTCDate | null =
      computed.end > availableDateRange[viewGranularity].max
        ? availableDateRange[viewGranularity].max
        : computed.end;

    if (finalStartDate > finalEndDate) {
      finalStartDate = null;
      finalEndDate = null;
    }

    return { startDate: finalStartDate, endDate: finalEndDate };
  } else {
    return { startDate: null, endDate: null };
  }
};

export const validateDateRange = (
  start: UTCDate | null,
  end: UTCDate | null,
  min: UTCDate | null,
  max: UTCDate | null
): string => {
  if (!start || !end) return '';

  if (start > end) return 'Start date must be before or same as end date';
  if (min && start < min) {
    return `Start date cannot be before system minimum (${min.toLocaleDateString()})`;
  }
  if (max && end > max) {
    return `End date cannot be after latest available (${max.toLocaleDateString()})`;
  }

  return '';
};
